// src/components/Admin/PendingApprovals.js
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Avatar,
  styled,
  LinearProgress,
  Grid,
  alpha,
  Checkbox,
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  Visibility,
  Email,
  Business,
  CalendarToday,
  Person,
  DoneAll,
} from '@mui/icons-material';
import { getPendingApprovals, approveUser, rejectUser, bulkApproveUsers } from '../../services/users';

const teslaColors = {
  primary: '#3E6AE1',
  secondary: '#34C3FF',
  success: '#4CAF50',
  error: '#f44336',
};

const GlassCard = styled(Card)(({ theme }) => ({
  background: theme.palette.mode === 'dark'
    ? 'rgba(30, 30, 30, 0.8)'
    : 'rgba(255, 255, 255, 0.8)',
  backdropFilter: 'blur(10px)',
  borderRadius: '16px',
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: theme.palette.mode === 'dark'
    ? '0 8px 32px 0 rgba(0, 0, 0, 0.36)'
    : '0 8px 32px 0 rgba(0, 0, 0, 0.1)',
}));

const StatsBox = ({ icon, label, value, color }) => (
  <Box sx={{ p: 2, borderRadius: '12px', background: alpha(color, 0.1), display: 'flex', alignItems: 'center', gap: 2 }}>
    <Box sx={{ width: 48, height: 48, borderRadius: '10px', background: `linear-gradient(45deg, ${color}, ${alpha(color, 0.7)})`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      {icon}
    </Box>
    <Box>
      <Typography variant="h5" fontWeight={700}>{value}</Typography>
      <Typography variant="body2" color="textSecondary">{label}</Typography>
    </Box>
  </Box>
);

function PendingApprovals() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [selected, setSelected] = useState([]);

  const fetchPendingUsers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getPendingApprovals();
      setUsers(response.users || []);
      setSelected([]);
    } catch (err) {
      setError('Failed to fetch pending approvals');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPendingUsers(); }, [fetchPendingUsers]);

  const isAllSelected = users.length > 0 && selected.length === users.length;
  const isIndeterminate = selected.length > 0 && selected.length < users.length;

  const handleSelectAll = (e) => setSelected(e.target.checked ? users.map(u => u._id) : []);
  const handleSelectOne = (id) => setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const handleApprove = async (userId) => {
    try {
      await approveUser(userId);
      setSuccess('User approved successfully');
      fetchPendingUsers();
    } catch (err) {
      setError(err.message || 'Failed to approve user');
    }
  };

  const handleBulkApprove = async () => {
    if (selected.length === 0) return;
    setBulkLoading(true);
    try {
      const result = await bulkApproveUsers(selected);
      setSuccess(result.message || `${selected.length} user(s) approved successfully`);
      fetchPendingUsers();
    } catch (err) {
      setError(err.message || 'Bulk approve failed');
    } finally {
      setBulkLoading(false);
    }
  };

  const handleRejectClick = (user) => { setSelectedUser(user); setRejectDialogOpen(true); };

  const handleRejectConfirm = async () => {
    if (!rejectionReason.trim() || rejectionReason.length < 10) {
      setError('Please provide a rejection reason (at least 10 characters)');
      return;
    }
    try {
      await rejectUser(selectedUser._id, rejectionReason);
      setSuccess('User rejected successfully');
      setRejectDialogOpen(false);
      setRejectionReason('');
      fetchPendingUsers();
    } catch (err) {
      setError(err.message || 'Failed to reject user');
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box mb={3} display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>Pending Approvals</Typography>
          <Typography variant="body2" color="textSecondary">Review and approve user registration requests</Typography>
        </Box>
        {selected.length > 0 && (
          <Button
            variant="contained" color="success" startIcon={<DoneAll />}
            onClick={handleBulkApprove} disabled={bulkLoading}
            sx={{ borderRadius: '12px', px: 3, fontWeight: 600 }}
          >
            {bulkLoading ? 'Approving...' : `Approve Selected (${selected.length})`}
          </Button>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3, borderRadius: '12px' }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 3, borderRadius: '12px' }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* Stats */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={4}>
          <StatsBox icon={<Person sx={{ color: 'white', fontSize: 24 }} />} label="Pending Approvals" value={users.length} color={teslaColors.primary} />
        </Grid>
        {selected.length > 0 && (
          <Grid item xs={12} sm={6} md={4}>
            <StatsBox icon={<CheckCircle sx={{ color: 'white', fontSize: 24 }} />} label="Selected" value={selected.length} color={teslaColors.success} />
          </Grid>
        )}
      </Grid>

      {/* Table */}
      <GlassCard>
        <CardContent>
          <Typography variant="h6" gutterBottom fontWeight={600}>Users Awaiting Approval</Typography>
          {loading ? <LinearProgress /> : users.length === 0 ? (
            <Box textAlign="center" py={8}>
              <CheckCircle sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="textSecondary" gutterBottom>No Pending Approvals</Typography>
              <Typography variant="body2" color="textSecondary">All user registration requests have been processed</Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox indeterminate={isIndeterminate} checked={isAllSelected} onChange={handleSelectAll} />
                    </TableCell>
                    <TableCell>User</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Facility</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Registered</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((user) => (
                    <TableRow
                      key={user._id} hover selected={selected.includes(user._id)}
                      onClick={() => handleSelectOne(user._id)} sx={{ cursor: 'pointer' }}
                    >
                      <TableCell padding="checkbox" onClick={e => e.stopPropagation()}>
                        <Checkbox checked={selected.includes(user._id)} onChange={() => handleSelectOne(user._id)} />
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1.5}>
                          <Avatar sx={{ width: 40, height: 40, background: `linear-gradient(45deg, ${teslaColors.primary}, ${teslaColors.secondary})` }}>
                            {user.firstName?.charAt(0)}{user.lastName?.charAt(0)}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight={500}>{user.firstName} {user.lastName}</Typography>
                            <Typography variant="caption" color="textSecondary">{user.phoneNumber || 'No phone'}</Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell><Typography variant="body2">{user.email}</Typography></TableCell>
                      <TableCell>
                        <Typography variant="body2">{user.facility?.facilityName || 'N/A'}</Typography>
                        {user.institutionRole && <Typography variant="caption" color="textSecondary" display="block">{user.institutionRole}</Typography>}
                      </TableCell>
                      <TableCell><Chip label={user.role || 'Clinician'} size="small" variant="outlined" /></TableCell>
                      <TableCell>{user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}</TableCell>
                      <TableCell align="right" onClick={e => e.stopPropagation()}>
                        <Tooltip title="View Details">
                          <IconButton size="small" onClick={() => { setSelectedUser(user); setDetailsDialogOpen(true); }}>
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Approve">
                          <IconButton size="small" color="success" onClick={() => handleApprove(user._id)}>
                            <CheckCircle fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Reject">
                          <IconButton size="small" color="error" onClick={() => handleRejectClick(user)}>
                            <Cancel fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </GlassCard>

      {/* User Details Dialog */}
      <Dialog
        open={detailsDialogOpen}
        onClose={() => setDetailsDialogOpen(false)}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            width: { xs: '95vw', sm: '680px', md: '720px' },
            maxWidth: '740px !important',
            maxHeight: '88vh',
            borderRadius: '14px',
          },
        }}
      >
        <DialogTitle sx={{ px: 2.5, py: 1.25 }}>
          <Typography variant="h6" fontWeight={700} sx={{ fontSize: '1.05rem' }}>User Details</Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ px: 2.5, py: 1.5 }}>
          {selectedUser && (
            <Box>
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <Avatar sx={{ width: 52, height: 52, background: `linear-gradient(45deg, ${teslaColors.primary}, ${teslaColors.secondary})`, fontSize: '1.1rem' }}>
                  {selectedUser.firstName?.charAt(0)}{selectedUser.lastName?.charAt(0)}
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight={700} sx={{ fontSize: '1rem' }}>{selectedUser.firstName} {selectedUser.lastName}</Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.82rem' }}>{selectedUser.role}</Typography>
                  <Chip label="Pending Approval" color="warning" size="small" sx={{ mt: 0.3, height: 20, fontSize: '0.7rem' }} />
                </Box>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Email sx={{ color: teslaColors.primary, fontSize: 18 }} />
                    <Box><Typography variant="caption" color="textSecondary">Email</Typography><Typography variant="body2" sx={{ fontSize: '0.85rem' }}>{selectedUser.email}</Typography></Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Business sx={{ color: teslaColors.primary, fontSize: 18 }} />
                    <Box><Typography variant="caption" color="textSecondary">Facility</Typography><Typography variant="body2" sx={{ fontSize: '0.85rem' }}>{selectedUser.facility?.facilityName || 'N/A'}</Typography></Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <CalendarToday sx={{ color: teslaColors.primary, fontSize: 18 }} />
                    <Box><Typography variant="caption" color="textSecondary">Registered</Typography><Typography variant="body2" sx={{ fontSize: '0.85rem' }}>{selectedUser.createdAt ? new Date(selectedUser.createdAt).toLocaleString() : 'N/A'}</Typography></Box>
                  </Box>
                </Grid>
                {selectedUser.phoneNumber && (
                  <Grid item xs={12} sm={6}>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <Person sx={{ color: teslaColors.primary, fontSize: 18 }} />
                      <Box><Typography variant="caption" color="textSecondary">Phone</Typography><Typography variant="body2" sx={{ fontSize: '0.85rem' }}>{selectedUser.phoneNumber}</Typography></Box>
                    </Box>
                  </Grid>
                )}
                {selectedUser.specialty && (
                  <Grid item xs={12}>
                    <Box><Typography variant="caption" color="textSecondary">Specialty</Typography><Typography variant="body2" sx={{ fontSize: '0.85rem' }}>{selectedUser.specialty}</Typography></Box>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.25 }}>
          <Button onClick={() => setDetailsDialogOpen(false)} size="small" sx={{ borderRadius: '8px', textTransform: 'none' }}>Close</Button>
          <Button variant="contained" color="error" size="small" startIcon={<Cancel sx={{ fontSize: 16 }} />} onClick={() => { setDetailsDialogOpen(false); handleRejectClick(selectedUser); }} sx={{ borderRadius: '8px', textTransform: 'none' }}>Reject</Button>
          <Button variant="contained" color="success" size="small" startIcon={<CheckCircle sx={{ fontSize: 16 }} />} onClick={() => { handleApprove(selectedUser._id); setDetailsDialogOpen(false); }} sx={{ borderRadius: '8px', textTransform: 'none' }}>Approve</Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog
        open={rejectDialogOpen}
        onClose={() => setRejectDialogOpen(false)}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            width: { xs: '95vw', sm: '500px' },
            maxWidth: '520px !important',
            maxHeight: '85vh',
            borderRadius: '14px',
          },
        }}
      >
        <DialogTitle sx={{ px: 2.5, py: 1.25 }}><Typography variant="h6" fontWeight={700} sx={{ fontSize: '1.05rem' }}>Reject User Registration</Typography></DialogTitle>
        <DialogContent dividers sx={{ px: 2.5, py: 1.5 }}>
          <Typography variant="body2" color="textSecondary" mb={1.5} sx={{ fontSize: '0.82rem' }}>Please provide a reason for rejecting this user's registration.</Typography>
          <TextField
            fullWidth multiline rows={3} size="small" label="Rejection Reason"
            value={rejectionReason} onChange={(e) => setRejectionReason(e.target.value)}
            placeholder="Please provide a detailed reason (minimum 10 characters)..."
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
            helperText={`${rejectionReason.length} / 500 characters (minimum 10 required)`}
          />
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.25 }}>
          <Button onClick={() => setRejectDialogOpen(false)} size="small" sx={{ borderRadius: '8px', textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" color="error" size="small" onClick={handleRejectConfirm} disabled={rejectionReason.length < 10} sx={{ borderRadius: '8px', textTransform: 'none', px: 2.5 }}>Confirm Rejection</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default PendingApprovals;
